import { AuthContext } from 'auth';
import * as React from 'react';
import { createContext, Dispatch, useContext, useReducer } from 'react';
import { FinancialsActions, getActions } from './actions';
import { FinancialsAction, financialsReducer } from './reducer';
import {
  defaultDepositFilters,
  defaultOutstandingFilters,
  defaultTransactionFilters,
  FinancialsState,
  LoadingType,
} from './types';

// Defines default state for the financials context
export const defaultFinancialsState: FinancialsState = {
  loading: {
    [LoadingType.GetOrganization]: false,
    [LoadingType.GetDeposits]: false,
    [LoadingType.GetOutstandingReports]: false,
    [LoadingType.GetTransactions]: false,
  },
  hasOrgAdminAccess: null,
  organization: null,
  depositFilters: defaultDepositFilters,
  deposits: [],
  depositSummary: null,
  outstandingReports: [],
  outstandingSummary: null,
  outstandingFilters: defaultOutstandingFilters,
  transactions: [],
  transactionSummary: null,
  transactionFilters: defaultTransactionFilters,
};

const FinancialsContext = createContext<FinancialsState>(
  defaultFinancialsState
);
const DispatchContext = createContext<Dispatch<FinancialsAction>>(undefined);

// Need to wrap whoever calls useFinancials in a FinancialsProvider, at the highest level
export const FinancialsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(
    financialsReducer,
    defaultFinancialsState
  );
  return (
    <FinancialsContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </FinancialsContext.Provider>
  );
};

// Hook to store and retrieve financials state across multiple components
export const useFinancials = (): FinancialsState => {
  const context = React.useContext(FinancialsContext);
  if (context === undefined) {
    throw new Error('useFinancials must be used within a FinancialsProvider');
  }
  return context;
};

// Hook to perform actions on the financials state across multiple components
// The dispatch talks to the financialsReducer to update the state
export const useFinancialsActions = (): FinancialsActions => {
  const dispatch = useContext(DispatchContext);
  const authContext = useContext(AuthContext);

  if (dispatch === undefined) {
    throw new Error(
      'useFinancialsActions must be used within a FinancialsProvider'
    );
  }
  return getActions(authContext, dispatch);
};
